<template>
  <div class="time-on-block" :class="`time-on-block--${phase}`">
    <div class="time-on-block__row">
      <span class="time-on-block__left">
        <span class="time-on-block__dot" />
        <span class="time-on-block__title">Time on block</span>
      </span>
      <span class="time-on-block__right">
        <span class="time-on-block__phase">{{ phaseLabel }}</span>
        <span class="time-on-block__clock">{{ clock }}</span>
      </span>
    </div>

    <div class="time-on-block__bar">
      <div class="time-on-block__fill" :style="{ width: `${ratio * 100}%` }" />
      <span class="time-on-block__tick" :style="{ left: `${(TARGET_SECONDS / MAXIMUM_SECONDS) * 100}%` }" />
      <span class="time-on-block__tick" :style="{ left: `${(WARN_SECONDS / MAXIMUM_SECONDS) * 100}%` }" />
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

// How long the current lot has been on the block, counting up, with a pace bar
// marked at the two thresholds.
//
// Pace is the whole point of the component: an auction that takes ninety
// seconds a car does not finish its run list, so the clerk is being told to
// move. Under 30s is on pace, 30–60s is a nudge, past 60s is a shout.
//
// The clock ticks locally, which is view state and not domain state — nothing
// outside this component can observe it and nothing is persisted. Where the
// count STARTS is the application's, because only it knows when the lot took
// the block.
defineOptions({ name: 'TimeOnBlock' });

const props = defineProps({
  // Seconds already elapsed when this mounts.
  startedAtSeconds: { type: Number, default: 0 },
});

const TARGET_SECONDS = 30;
const WARN_SECONDS = 60;
const MAXIMUM_SECONDS = 90;

const seconds = ref(props.startedAtSeconds);
let timer = null;
onMounted(() => {
  timer = setInterval(() => {
    seconds.value += 1;
  }, 1000);
});
onBeforeUnmount(() => clearInterval(timer));

const ratio = computed(() => Math.min(1, seconds.value / MAXIMUM_SECONDS));

const phase = computed(() => {
  if (seconds.value < TARGET_SECONDS) return 'calm';
  if (seconds.value < WARN_SECONDS) return 'warn';
  return 'urgent';
});

const PHASE_LABELS = { calm: 'On pace', warn: 'Keep moving', urgent: 'Move it' };
const phaseLabel = computed(() => PHASE_LABELS[phase.value]);

const clock = computed(
  () => `${Math.floor(seconds.value / 60)}:${String(seconds.value % 60).padStart(2, '0')}`
);
</script>

<style scoped>
.time-on-block {
  display: flex;
  flex-direction: column;
  gap: var(--p-time-on-block-gap);
  padding: var(--p-time-on-block-padding);
  border-radius: var(--p-time-on-block-border-radius);
  border: var(--p-time-on-block-border-width) solid var(--p-time-on-block-border-color);
  background: var(--p-time-on-block-background);
}

.time-on-block--warn {
  background: var(--p-time-on-block-warn-background);
  border-color: var(--p-time-on-block-warn-border-color);
}

.time-on-block--urgent {
  background: var(--p-time-on-block-urgent-background);
  border-color: var(--p-time-on-block-urgent-border-color);
}

.time-on-block__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.time-on-block__left {
  display: inline-flex;
  align-items: center;
  gap: var(--p-time-on-block-left-gap);
}

.time-on-block__dot {
  width: 8px;
  height: 8px;
  border-radius: var(--p-time-on-block-dot-border-radius);
  background: var(--p-time-on-block-dot-background);
}

/* Past the target the dot lights and lifts off the surface; past the warning it
   pulses, which is the only motion on this card and so cannot be missed. */
.time-on-block--warn .time-on-block__dot {
  background: var(--p-time-on-block-warn-dot-background);
  box-shadow: var(--p-time-on-block-warn-dot-box-shadow);
}

.time-on-block--urgent .time-on-block__dot {
  background: var(--p-time-on-block-urgent-dot-background);
  box-shadow: var(--p-time-on-block-urgent-dot-box-shadow);
  animation: time-on-block-pulse 1.3s infinite;
}

.time-on-block__title {
  font-size: var(--p-time-on-block-title-font-size);
  font-weight: var(--p-time-on-block-title-font-weight);
  letter-spacing: var(--p-time-on-block-title-letter-spacing);
  color: var(--p-time-on-block-title-color);
  text-transform: uppercase;
}

.time-on-block__right {
  display: inline-flex;
  align-items: baseline;
  gap: var(--p-time-on-block-right-gap);
}

.time-on-block__phase {
  font-size: var(--p-time-on-block-phase-font-size);
  font-weight: var(--p-time-on-block-phase-font-weight);
  letter-spacing: var(--p-time-on-block-phase-letter-spacing);
  color: var(--p-time-on-block-phase-color);
  text-transform: uppercase;
}

.time-on-block--warn .time-on-block__phase {
  color: var(--p-time-on-block-warn-phase-color);
}

.time-on-block--urgent .time-on-block__phase {
  color: var(--p-time-on-block-urgent-phase-color);
}

.time-on-block__clock {
  font-family: var(--p-time-on-block-clock-font-family);
  font-size: var(--p-time-on-block-clock-font-size);
  font-weight: var(--p-time-on-block-clock-font-weight);
  letter-spacing: var(--p-time-on-block-clock-letter-spacing);
  color: var(--p-time-on-block-clock-color);
  font-variant-numeric: tabular-nums;
}

.time-on-block--warn .time-on-block__clock {
  color: var(--p-time-on-block-warn-clock-color);
}

.time-on-block--urgent .time-on-block__clock {
  color: var(--p-time-on-block-urgent-clock-color);
}

.time-on-block__bar {
  height: 4px;
  overflow: hidden;
  position: relative;
  border-radius: var(--p-time-on-block-bar-border-radius);
  background: var(--p-time-on-block-bar-background);
}

.time-on-block__fill {
  height: 100%;
  transition: all 400ms cubic-bezier(0.2, 0, 0, 1);
  background: var(--p-time-on-block-fill-background);
}

.time-on-block--warn .time-on-block__fill {
  background: var(--p-time-on-block-warn-fill-background);
}

.time-on-block--urgent .time-on-block__fill {
  background: var(--p-time-on-block-urgent-fill-background);
}

.time-on-block__tick {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--p-time-on-block-tick-background);
}

@keyframes time-on-block-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(0.85);
    opacity: 0.65;
  }
}

</style>
